/* ============================================================
   VITALCASA CRM — ModalEmail (tema claro/oscuro)
   Modal para envío de Email desde el CRM.
   
   Dos modos:
   - Con propiedades seleccionadas: envía email HTML con fotos
   - Mensaje directo: solo texto libre + respuestas rápidas
   
   Props:
   - isOpen: boolean
   - onClose: () => void
   - onSend: ({ contacto, propiedades, mensaje }) => void
   - contact: { nombre, email, iniciales }
   - properties?: [{ id, titulo, detalle, precio, icono, tipo, poblacion, zona,
                      habitaciones, banos, m2, referencia }]
   - idioma?: string — idioma del cliente (traduce respuestas rápidas si no es ES)
   ============================================================ */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import api from '../services/api';
import { buildWhatsAppQuickReplies, normalizeWaIdioma } from '../utils/whatsappQuickReplies';
import { buildGestionVentasQuickReplies, nombreDocumentoLegible } from '../utils/gestionVentasQuickReplies';
import { mostrarToastEnvioPropiedades, mostrarToastVitalCasa } from '../utils/mostrarToastVitalCasa';
import {
  aplicarPlaceholdersMensaje,
  nombreParaPlantillaMensaje,
  primerNombre,
} from '../utils/nombrePlantillaMensaje';

// ── Colores Email (naranja/ámbar) ───────────────────────────
const em = {
  orange:       '#d97706',
  orangeFrom:   '#b45309',
  orangeTo:     '#d97706',
  orangeLight:  '#f59e0b',
  orangeText:   '#fef3c7',
  orangeShadow: 'rgba(217,119,6,0.35)',
  orangeBg:     'rgba(217,119,6,0.08)',
  orangeBorder: 'rgba(217,119,6,0.20)',
  chipText:     '#fbbf24',
};

export default function ModalEmail({ isOpen, onClose, onSend, contact, properties = [], allowManualRecipient = false, crmContext = null, idioma = 'ES', documentosNas = null, quickRepliesExpediente = null }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';

  const [asunto, setAsunto] = useState(
    properties.length > 0 ? 'Propiedades seleccionadas para ti - VitalCasa' : ''
  );
  const [mensaje, setMensaje] = useState('');
  const [propsAdjuntas, setPropsAdjuntas] = useState(properties);
  const [enviado, setEnviado] = useState(false);
  const [msgFocused, setMsgFocused] = useState(false);
  const [asuntoFocused, setAsuntoFocused] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualNombre, setManualNombre] = useState('');
  const [videosCatalog, setVideosCatalog] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videoSeleccion, setVideoSeleccion] = useState('ninguno');
  const [agentePrimerNombre, setAgentePrimerNombre] = useState('');
  const [agenteNombreCompleto, setAgenteNombreCompleto] = useState('');
  const [agenteSexo, setAgenteSexo] = useState('');
  const [quickRepliesFromApi, setQuickRepliesFromApi] = useState(null);
  const [quickRepliesGv, setQuickRepliesGv] = useState(null);

  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const propIdsKey = propsAdjuntas.map(p => p.id).filter(Boolean).sort().join(',');
  const propertiesIdsKey = properties.map((p) => p?.id).filter(Boolean).sort().join(',');

  // Sync adjuntas solo si cambian los ids (el padre suele pasar un array nuevo cada render).
  useEffect(() => {
    if (!isOpen) return;
    if (propertiesIdsKey === propIdsKey) return;
    setPropsAdjuntas(properties);
  }, [isOpen, propertiesIdsKey, propIdsKey, properties]);

  // Solo depende de propIdsKey: evita cancelar la carga al re-renderizar el padre.
  useEffect(() => {
    if (!isOpen || !propIdsKey) {
      setVideosCatalog([]);
      setVideoSeleccion('ninguno');
      setVideosLoading(false);
      return;
    }
    const snapshot = propsAdjuntas.filter((p) => p?.id);
    let cancelled = false;
    setVideosLoading(true);
    Promise.all(
      snapshot.map((p) =>
        api
          .get(`/propiedades/${p.id}/videos`)
          .then((r) => ({ prop: p, videos: r.data?.data || [] }))
          .catch(() => ({ prop: p, videos: [] })),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const flat = [];
        for (const { prop: p, videos } of results) {
          const ref = (p.referencia && String(p.referencia).trim()) || '';
          (videos || []).forEach((v, idx) => {
            flat.push({
              ...v,
              propiedad_id: p.id,
              label: `${ref || (p.titulo || 'Prop.').slice(0, 28)} · ${(v.titulo && v.titulo.trim()) || `Vídeo ${idx + 1}`}`,
            });
          });
        }
        setVideosCatalog(flat);
      })
      .finally(() => {
        if (!cancelled) setVideosLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot al cambiar ids
  }, [isOpen, propIdsKey]);

  useEffect(() => {
    if (!videosCatalog.length) {
      setVideoSeleccion('ninguno');
      return;
    }
    setVideoSeleccion((prev) => {
      if (prev === 'ninguno' || prev === 'todos') return prev;
      return videosCatalog.some((v) => v.id === prev) ? prev : 'ninguno';
    });
  }, [videosCatalog]);

  function videoEnvioPayload() {
    if (videoSeleccion === 'todos') return { modo: 'todos' };
    if (videoSeleccion === 'ninguno' || !videosCatalog.length) return { modo: 'ninguno' };
    return { modo: 'uno', video_id: videoSeleccion };
  }

  useEffect(() => {
    if (isOpen) {
      setManualEmail(contact?.email || '');
      setManualNombre(contact?.nombre || '');
      if (documentosNas?.archivos?.length) {
        setAsunto(`Documentación expediente ${documentosNas.numeroExpediente || ''}`.trim());
        setMensaje('');
      } else if (properties.length > 0) {
        setAsunto('Propiedades seleccionadas para ti - VitalCasa');
      }
    }
  }, [isOpen, properties, contact, documentosNas]);

  useEffect(() => {
    if (!isOpen) return;
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || '{}');
      setAgentePrimerNombre(primerNombre(u.nombre || ''));
      setAgenteNombreCompleto(String(u.nombre || '').trim());
      setAgenteSexo(String(u.sexo || '').trim());
    } catch {
      setAgentePrimerNombre('');
      setAgenteNombreCompleto('');
      setAgenteSexo('');
    }
    api.get('/auth/me').then((r) => {
      const u = r.data?.usuario || r.data;
      const n = u?.nombre || r.data?.nombre;
      if (n) {
        setAgentePrimerNombre(primerNombre(n));
        setAgenteNombreCompleto(String(n).trim());
      }
      const sx = u?.sexo || r.data?.sexo;
      if (sx) setAgenteSexo(String(sx).trim());
    }).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuickRepliesFromApi(null);
      setQuickRepliesGv(null);
      return;
    }
    if (quickRepliesExpediente) {
      setQuickRepliesFromApi(null);
      const archivos = documentosNas?.archivos?.length
        ? documentosNas.archivos
        : (quickRepliesExpediente.archivos || []);
      setQuickRepliesGv(buildGestionVentasQuickReplies({
        nombreUsuario: agenteNombreCompleto,
        sexo: agenteSexo,
        poblacion: quickRepliesExpediente.poblacion,
        direccion: quickRepliesExpediente.direccion,
        archivos,
      }));
      return;
    }
    setQuickRepliesGv(null);
    let cancelled = false;
    const count = propsAdjuntas.length;
    const idiomaWa = normalizeWaIdioma(idioma);
    api
      .get('/whatsapp-plantillas/runtime', { params: { count, idioma: idiomaWa } })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.ok && Array.isArray(r.data.replies)) {
          setQuickRepliesFromApi(r.data.replies);
        } else {
          setQuickRepliesFromApi(buildWhatsAppQuickReplies(count, idiomaWa));
        }
      })
      .catch(() => {
        if (!cancelled) setQuickRepliesFromApi(buildWhatsAppQuickReplies(count, idiomaWa));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, propsAdjuntas.length, idioma, quickRepliesExpediente, documentosNas, agenteNombreCompleto, agenteSexo]);

  if (!isOpen) return null;

  const idiomaWa = normalizeWaIdioma(idioma);
  const quickRepliesList = quickRepliesGv
    || quickRepliesFromApi
    || buildWhatsAppQuickReplies(propsAdjuntas.length, idiomaWa);
  const hasProps = propsAdjuntas.length > 0;
  const hasDocsNas = Boolean(documentosNas?.expedienteId && documentosNas?.archivos?.length);

  const nombreClienteParaPlantilla = () => {
    const raw = allowManualRecipient ? manualNombre.trim() : String(contact?.nombre || '').trim();
    return nombreParaPlantillaMensaje(raw);
  };

  const mensajeConPlaceholdersResuelto = () =>
    aplicarPlaceholdersMensaje(mensaje, nombreClienteParaPlantilla(), agentePrimerNombre);

  const removeProp = (id) => {
    setPropsAdjuntas(prev => prev.filter(p => p.id !== id));
  };

  const applyQuickReply = (text) => {
    const body = quickRepliesExpediente
      ? text
      : aplicarPlaceholdersMensaje(text, nombreClienteParaPlantilla(), agentePrimerNombre);
    setMensaje(body.slice(0, 2000));
  };

  const handleSend = async () => {
    const email = allowManualRecipient ? manualEmail.trim() : (contact?.email || '');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorEnvio(allowManualRecipient ? 'Indica un email válido' : 'El cliente no tiene email');
      return;
    }

    setEnviando(true);
    setErrorEnvio('');

    const mensajeEnviar = mensajeConPlaceholdersResuelto().slice(0, 2000);
    const landingBase = import.meta.env.VITE_LANDING_URL || 'https://movil.vitalcasa.com';
    const crmPayload = {};
    if (crmContext?.lead_id) crmPayload.lead_id = crmContext.lead_id;
    if (crmContext?.cliente_id) crmPayload.cliente_id = crmContext.cliente_id;

    let agenteIdEnvio = '';
    try {
      const raw = localStorage.getItem('usuario');
      const u = raw ? JSON.parse(raw) : null;
      agenteIdEnvio = (u?.agente_id || '').toString().trim();
    } catch { /* noop */ }

    try {
      if (hasDocsNas) {
        const { data } = await api.post('/email/enviar-adjuntos-nas', {
          destinatario: email,
          asunto: asunto || `Documentación expediente ${documentosNas.numeroExpediente || ''}`.trim(),
          mensaje: mensajeEnviar || `Adjunto documentación del expediente ${documentosNas.numeroExpediente || ''}.`.trim(),
          expediente_id: documentosNas.expedienteId,
          archivos: documentosNas.archivos,
          ...crmPayload,
        }, { timeout: 120000 });

        if (!data.ok) {
          setErrorEnvio(data.error || 'Error enviando email');
          setEnviando(false);
          mostrarToastVitalCasa('No se ha podido enviar el email', {
            tipo: 'error',
            detalle: data.error || 'Error enviando email',
            duracionMs: 2800,
          });
          return;
        }
      } else if (hasProps) {
        const { data } = await api.post('/email/enviar-propiedades', {
          destinatario: email,
          asunto: asunto || 'Propiedades seleccionadas - VitalCasa',
          mensaje_intro: mensajeEnviar || null,
          video_envio: videoEnvioPayload(),
          ...crmPayload,
          propiedades: propsAdjuntas.map(p => {
            const ref = p.referencia ? String(p.referencia).trim().toLowerCase().replace(/\s+/g, '-') : '';
            const base = `${landingBase.replace(/\/$/, '')}/propiedad/${ref}`;
            const link = ref
              ? (agenteIdEnvio ? `${base}?a=${encodeURIComponent(agenteIdEnvio)}` : base)
              : '';
            return {
              id: p.id,
              titulo: p.titulo,
              tipo: p.tipo || '',
              poblacion: p.poblacion || '',
              zona: p.zona || '',
              precio: p.precio,
              habitaciones: p.habitaciones || null,
              banos: p.banos || null,
              m2: p.m2 || null,
              referencia: p.referencia || '',
              link,
            };
          }),
        });

        if (!data.ok) {
          setErrorEnvio(data.error || 'Error enviando email');
          setEnviando(false);
          mostrarToastEnvioPropiedades({
            ok: false,
            canal: 'email',
            count: propsAdjuntas.length,
            mensaje: data.error || 'Error enviando email',
          });
          return;
        }
      } else {
        const { data } = await api.post('/email/enviar', {
          destinatario: email,
          asunto: asunto || 'VitalCasa',
          mensaje: mensajeEnviar,
          ...crmPayload,
          ...(propsAdjuntas.length > 0
            ? { propiedades_ids: propsAdjuntas.map((p) => p.id).filter(Boolean) }
            : {}),
        });

        if (!data.ok) {
          setErrorEnvio(data.error || 'Error enviando email');
          setEnviando(false);
          mostrarToastVitalCasa('No se ha podido enviar el email', {
            tipo: 'error',
            detalle: data.error || 'Error enviando email',
            duracionMs: 2800,
          });
          return;
        }
      }

      if (hasProps) {
        mostrarToastEnvioPropiedades({
          ok: true,
          canal: 'email',
          count: propsAdjuntas.length,
        });
      } else {
        mostrarToastVitalCasa('Email enviado correctamente', { tipo: 'ok', duracionMs: 2000 });
      }

      onSend?.({
        contacto: allowManualRecipient ? { ...contact, nombre: manualNombre.trim() || contact?.nombre, email } : contact,
        propiedades: propsAdjuntas,
        mensaje: mensajeEnviar,
      });

      setEnviando(false);
      setEnviado(true);
      setTimeout(() => {
        setEnviado(false);
        setMensaje('');
        setAsunto('');
        onClose?.();
      }, 1800);
    } catch (e) {
      console.error('Error enviando email:', e);
      const msg =
        e.response?.data?.error ||
        (e.code === 'ECONNABORTED' ? 'La petición tardó demasiado. Prueba con menos propiedades o revisa la conexión.' : null) ||
        e.message ||
        'No se pudo conectar con el servidor';
      setErrorEnvio(msg);
      setEnviando(false);
      if (hasProps) {
        mostrarToastEnvioPropiedades({
          ok: false,
          canal: 'email',
          count: propsAdjuntas.length,
          mensaje: msg,
        });
      } else {
        mostrarToastVitalCasa('No se ha podido enviar el email', {
          tipo: 'error',
          detalle: msg,
          duracionMs: 2800,
        });
      }
    }
  };

  const propCount = propsAdjuntas.length;
  const layoutCompact = hasProps && propCount >= 3;
  const modalMaxHeight = layoutCompact
    ? 'min(96vh, 820px)'
    : hasProps && propCount >= 2
      ? 'min(94vh, 760px)'
      : 'min(92vh, 720px)';
  const sectionMargin = layoutCompact ? '3px 10px 0' : '6px 10px 0';
  const sectionPad = layoutCompact ? '5px 10px' : '8px 12px';
  const labelMb = layoutCompact ? 4 : 8;

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 480, maxHeight: modalMaxHeight, display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10), 0 0 200px 80px rgba(255,255,255,0.05), 0 0 300px 120px rgba(255,255,255,0.02)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>

        {/* Confirmación de envío */}
        {enviado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: em.orange }}>
              {propsAdjuntas.length > 1
                ? 'Propiedades enviadas'
                : propsAdjuntas.length === 1
                  ? 'Propiedad enviada'
                  : 'Email enviado'}
            </div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500 }}>Correo entregado a {allowManualRecipient ? (manualNombre.trim() || email) : (contact?.nombre || email)}</div>
          </div>
        )}

        {/* Barra naranja superior */}
        <div style={{
          height: 3, flexShrink: 0,
          background: `linear-gradient(90deg, ${em.orangeLight}, ${em.orange}, ${em.orangeFrom}, ${em.orange}, ${em.orangeLight})`,
        }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{ padding: layoutCompact ? '8px 12px' : '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: layoutCompact ? 10 : 12 }}>
            <div style={{
              width: layoutCompact ? 36 : 40, height: layoutCompact ? 36 : 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${em.orangeFrom}, ${em.orangeTo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, border: `1px solid ${c.border}`,
              boxShadow: `0 4px 12px ${em.orangeShadow}`,
            }}>✉️</div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Enviar Email</h2>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, fontWeight: 500, color: c.textMuted }}>
                {hasDocsNas
                  ? `${documentosNas.archivos.length} documento(s) del expediente`
                  : hasProps
                    ? `${propsAdjuntas.length} propiedad${propsAdjuntas.length > 1 ? 'es' : ''} seleccionada${propsAdjuntas.length > 1 ? 's' : ''}`
                    : 'Mensaje directo'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Cuerpo scrollable */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: isDark ? 0 : '2px 0 4px' }}>

          {/* Destinatario */}
          <div style={{ margin: sectionMargin, padding: sectionPad, background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${em.orange}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'block', marginBottom: labelMb, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : em.orange }}>Destinatario</div>
            {allowManualRecipient ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="text"
                  placeholder="Nombre (opcional)"
                  value={manualNombre}
                  onChange={e => setManualNombre(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: `2px solid ${c.border}`, background: c.bgInput, color: c.textPrimary,
                    fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                />
                <input
                  type="email"
                  placeholder="Correo electrónico"
                  value={manualEmail}
                  onChange={e => setManualEmail(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: `2px solid ${em.orangeBorder}`, background: c.bgInput, color: c.textPrimary,
                    fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: layoutCompact ? '7px 12px' : '10px 14px', borderRadius: 10,
                border: `2px solid ${c.border}`, background: c.bgInput,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${em.orangeFrom}, ${em.orangeTo})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>{contact?.iniciales || '??'}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.textPrimary }}>{contact?.nombre || 'Sin nombre'}</div>
                  <div style={{ fontSize: 12, color: c.textMuted, fontWeight: 500, marginTop: 1 }}>{contact?.email || 'Sin email'}</div>
                </div>
              </div>
            )}
          </div>

          {hasDocsNas && (
            <div style={{
              margin: sectionMargin,
              padding: '6px 10px',
              borderRadius: 8,
              background: isDark ? 'rgba(56,189,248,0.12)' : 'rgba(217,119,6,0.12)',
              border: `1px solid ${isDark ? 'rgba(56,189,248,0.35)' : em.orangeBorder}`,
              fontSize: 11,
              color: isDark ? '#7dd3fc' : em.orangeFrom,
            }}>
              Se adjuntarán {documentosNas.archivos.length} documento(s): {documentosNas.archivos.map(nombreDocumentoLegible).join(', ')}
            </div>
          )}

          {/* Asunto */}
          <div style={{ margin: sectionMargin, padding: sectionPad, background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${em.orange}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'block', marginBottom: labelMb, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : em.orange }}>Asunto</div>
            <input
              type="text"
              style={{
                width: '100%', padding: layoutCompact ? '8px 12px' : '10px 14px', borderRadius: 10,
                border: `2px solid ${asuntoFocused ? em.orange : c.border}`,
                background: c.bgInput, color: c.textPrimary,
                fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit',
                outline: 'none', transition: 'border-color 0.2s',
              }}
              placeholder="Asunto del email..."
              value={asunto}
              onChange={e => setAsunto(e.target.value)}
              onFocus={() => setAsuntoFocused(true)}
              onBlur={() => setAsuntoFocused(false)}
            />
          </div>

          {/* Propiedades adjuntas */}
          {hasProps && (
            <div style={{ margin: sectionMargin, padding: sectionPad, background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${em.orange}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'block', marginBottom: labelMb, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : em.orange }}>Propiedades adjuntas</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: layoutCompact ? 4 : 6 }}>
                {propsAdjuntas.map(prop => (
                  <div key={prop.id} style={{
                    display: 'flex', alignItems: 'center', gap: layoutCompact ? 8 : 10,
                    padding: layoutCompact ? '4px 8px' : '7px 10px', borderRadius: 8,
                    background: isDark ? c.bgInput : 'rgba(217,119,6,0.12)',
                    border: `1px solid ${isDark ? c.border : 'rgba(217,119,6,0.35)'}`,
                    borderLeft: `4px solid ${em.orange}`,
                  }}>
                    <div style={{
                      width: layoutCompact ? 40 : 48, height: layoutCompact ? 28 : 36, borderRadius: 6,
                      background: c.bgCard, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: layoutCompact ? 14 : 16, color: c.textMuted, border: `1px solid ${c.borderLight}`,
                      overflow: 'hidden',
                    }}>{prop.icono || '🏠'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: layoutCompact ? 12.5 : 13, fontWeight: 600, color: c.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prop.titulo}</div>
                      <div style={{ fontSize: layoutCompact ? 11 : 11.5, color: c.textMuted, fontWeight: 500, marginTop: layoutCompact ? 1 : 2 }}>
                        {(prop.referencia || prop.ref) ? `Ref. ${String(prop.referencia || prop.ref).trim()}` : null}
                      </div>
                    </div>
                    <div style={{ fontSize: layoutCompact ? 12 : 13, fontWeight: 700, color: em.orange, flexShrink: 0 }}>{prop.precio}</div>
                    <div onClick={() => removeProp(prop.id)} style={{
                      width: 24, height: 24, borderRadius: 6,
                      border: `1px solid ${c.border}`, background: c.bgInput,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, color: c.textMuted, cursor: 'pointer', flexShrink: 0,
                    }}>✕</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mensaje */}
          <div style={{ margin: sectionMargin, padding: sectionPad, background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${em.orange}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'block', marginBottom: labelMb, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : em.orange }}>Mensaje</div>
            <textarea
              className="modal-em-textarea"
              style={{
                width: '100%',
                minHeight: layoutCompact ? 44 : (hasProps ? 52 : 72),
                maxHeight: layoutCompact ? 64 : (hasProps ? 90 : 120),
                padding: layoutCompact ? '6px 10px' : '8px 12px', borderRadius: 8,
                border: `1.5px solid ${msgFocused ? em.orange : (isDark ? c.border : '#a8a8a0')}`,
                background: isDark ? c.bgInput : 'rgba(217,119,6,0.12)', color: c.textPrimary,
                fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit',
                outline: 'none', resize: hasProps ? 'none' : 'vertical', transition: 'border-color 0.2s',
              }}
              placeholder={hasDocsNas ? 'Escribe el mensaje que acompañará los documentos...' : hasProps ? 'Escribe un mensaje personalizado (opcional)...' : 'Escribe tu mensaje...'}
              value={mensaje}
              onChange={e => setMensaje(e.target.value.slice(0, 2000))}
              onFocus={() => setMsgFocused(true)}
              onBlur={() => setMsgFocused(false)}
            />
            <div style={{ textAlign: 'right', fontSize: 10, color: c.textMuted, fontWeight: 500, marginTop: layoutCompact ? 2 : 4, opacity: 0.5 }}>
              {mensaje.length} / 2.000
            </div>

            {/* Respuestas rápidas (mismas que WhatsApp, traducidas al idioma del cliente) */}
            <>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.textMuted, marginTop: layoutCompact ? 4 : 6, marginBottom: layoutCompact ? 3 : 4, opacity: 0.6 }}>
                Respuestas rápidas
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: layoutCompact ? 3 : 4 }}>
                {quickRepliesList.map(qr => (
                  <button key={qr.id} type="button" onClick={() => applyQuickReply(qr.text)} style={{
                    padding: '4px 8px', borderRadius: 5,
                    border: `1px solid ${em.orangeBorder}`, background: em.orangeBg,
                    color: em.chipText, fontSize: 10, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>{qr.emoji} {qr.label}</button>
                ))}
              </div>
            </>
          </div>

          {hasProps && (videosLoading || videosCatalog.length > 0) && (
            <div style={{
              margin: layoutCompact ? '2px 10px 0' : '4px 10px 0',
              padding: layoutCompact ? '4px 8px' : '6px 10px',
              borderRadius: 8,
              border: `1px solid ${em.orangeBorder}`,
              background: em.orangeBg,
            }}>
              <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.textMuted, marginBottom: 4 }}>
                Vídeos en el envío
              </div>
              {videosLoading ? (
                <div style={{ fontSize: 11, color: c.textMuted }}>Cargando…</div>
              ) : (
                <select
                  value={videoSeleccion}
                  onChange={(e) => setVideoSeleccion(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: `1px solid ${c.border}`,
                    background: c.bgInput,
                    color: c.textPrimary,
                    fontSize: 11,
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="ninguno">No incluir vídeos</option>
                  <option value="todos">Todos los vídeos</option>
                  {videosCatalog.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div style={{ height: layoutCompact ? 2 : 4 }} />
        </div>

        {/* Error de envío */}
        {errorEnvio && (
          <div style={{ padding: '0 24px 8px', fontSize: 12, color: '#ef4444', fontWeight: 600, textAlign: 'center' }}>
            ⚠️ {errorEnvio}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '10px 14px 12px',
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          flexShrink: 0, borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          background: isDark ? 'transparent' : '#e0e0d8',
        }}>
          <button onClick={onClose} style={{
            padding: '11px 24px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
            background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary,
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          }}>Cancelar</button>
          <button onClick={handleSend} disabled={enviando} style={{
            padding: '11px 30px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${em.orangeFrom}, ${em.orangeTo})`,
            color: em.orangeText, cursor: enviando ? 'wait' : 'pointer', fontFamily: 'inherit',
            fontSize: 13, fontWeight: 700,
            boxShadow: `0 4px 14px ${em.orangeShadow}`,
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: enviando ? 0.7 : 1,
          }}>
            <span style={{ fontSize: 15 }}>📧</span>
            {enviando ? 'Enviando...' : 'Enviar Email'}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
